import { useState, useMemo, useEffect } from 'react';
import { useUpdateAccountLimit } from '@/features/card/hooks/useCard/useUpdateAccountLimit';
import { formatCurrencyInput } from '@/features/card/components/ActivateCardModal/activateCard.validators';
import { Card } from '@/features/card/types/card.types';
import { LimitOperation } from '@/features/card/types/account.types';
import { useAuthContext } from '@/features/auth/context/AuthContext/AuthContext';
import { useToast, useSpinner } from '@chikara/ui';
import { customerService } from '@/features/card/services/customerService';
import { t } from '@/i18n';

interface UseManageLimitFormProps {
    isOpen: boolean;
    onClose: () => void;
    card: Card;
    onSuccess: () => void;
}

export const useManageLimitForm = ({ isOpen, onClose, card, onSuccess }: UseManageLimitFormProps) => {
    const toast = useToast();
    const { show, hide } = useSpinner();
    const { payload: authPayload } = useAuthContext();
    const { updateLimit } = useUpdateAccountLimit();
    const PERCENT = 100;

    const [limit, setLimit] = useState<number>(card.creditLimit);
    const [inputValue, setInputValue] = useState<string>('');
    const [globalAvailable, setGlobalAvailable] = useState<number>(0);
    const [totalAvailableToAssign, setTotalAvailableToAssign] = useState<number>(0);

    const [localError, setLocalError] = useState<string | null>(null);

    useEffect(() => {
        const fetchSummary = async () => {
            if (isOpen && authPayload?.customer_uuid) {
                show();
                try {
                    const response = await customerService.getCustomerSummary();
                    setGlobalAvailable(response.credit_line.total_limit);
                    setTotalAvailableToAssign(response.credit_line.total_available_to_assign);
                } catch (error) {
                    console.error('Error fetching customer summary:', error);
                } finally {
                    hide();
                }
            }
        };

        fetchSummary();
    }, [authPayload?.customer_uuid, show, hide, isOpen]);

    useEffect(() => {
        if (isOpen && card.account_uuid) {
            setLimit(card.creditLimit);
            setInputValue(formatCurrencyInput(Math.round(card.creditLimit * PERCENT).toString()));
            setLocalError(null);
        }
    }, [isOpen, card.account_uuid, card.creditLimit]);

    const currentCardLimit = card.creditLimit;
    const maxAvailableToAssign = totalAvailableToAssign + currentCardLimit;
    const availableToAssign = maxAvailableToAssign - limit < 0 ? 0 : maxAvailableToAssign - limit;

    const isLimitExceeded = useMemo(() => {
        return limit > maxAvailableToAssign;
    }, [limit, maxAvailableToAssign]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const numeric = value.replace(/[^\d]/g, '');
        const number = Number(numeric) / PERCENT;

        setInputValue(formatCurrencyInput(numeric));
        setLimit(number);

        const maxAvailable = totalAvailableToAssign + currentCardLimit;

        if (number > maxAvailable) {
            setLocalError(t('dashboard.card.detail.modals.manageLimit.errors.exceeded', { amount: maxAvailable }));
        } else {
            setLocalError(null);
        }
    };

    const handleSave = async () => {
        if (isLimitExceeded) return;

        const operation: LimitOperation = !currentCardLimit
            ? 'set'
            : limit > currentCardLimit ? 'increment' : 'decrement';

        const success = await updateLimit({ customer_uuid: authPayload.customer_uuid || '', account_uuid: card.account_uuid, amount: limit, operation });
        if (success) {
            toast.success(t('dashboard.card.detail.modals.manageLimit.success'));
            onSuccess();
            onClose();
        }
    };

    const isActionDisabled = useMemo(() => {
        return isLimitExceeded || limit === currentCardLimit;
    }, [isLimitExceeded, limit, currentCardLimit]);

    return {
        inputValue,
        globalAvailable,
        availableToAssign,
        localError,
        isActionDisabled,
        handleInputChange,
        handleSave,
    };
};
